import CustomChip from './CustomChip';
import { Autocomplete, Box, Popper, TextField } from '@mui/material';
import {CustomChipAutocompleteStyles as styles, CustomStyleFormHelperText} from './Styles';
import { useEffect, useRef, useState } from 'react';
import { colors } from '../enums/colors';
import { ArrowAccordionIcon, ArrowAccordionUpIcon } from './buttons/IconButtons';
import { CustomSearch, PublicCustomSearch } from '../reduxToolkit/Commons/CommonsApi';
import { FormHelperText } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DEBOUNCE_DELAY } from '../Apis/functions';

/**
 * @Author: Susana Garcia Rodriguez
 * @JobTitle: Frontend - IBL
 * @Email: sgarcia@ibl.mx
 *
 * @Description Autocomplete with multiple selection displayed as status chips.
 * @param {Array} options - List of selectable options returned from the API or local source (required)
 * @param {Function} setOptions - Setter function used to update option results, especially after API search (required)
 * @param {Array} value - Controlled array of selected values (optional)
 * @param {Function} onChange - Callback executed when selected values change (optional)
 * @param {Function} getOptionLabel - Function that extracts the label used for display and filtering (required)
 * @param {String} placeholder - Text placeholder shown inside the input field (optional)
 * @param {Array} userTypeIdsSearch - Array of user type IDs sent as filters to the API request (optional)
 * @param {String} api - API endpoint URL used for dynamic searching (required when API search is needed)
 * @param {Object|null} bodyApi - Custom base request body to be merged with the search key. If null, a default body structure is used (optional)
 * @param {Number} rowsPerPage - Number of results to request per API call (default: 20)
 * @param {String|Number} model - Identifier sent to the backend to indicate the search model or entity type (optional)
 * @param {String} keyId - Unique key field name used to build option objects (required)
 * @param {Boolean} showSubSection - Enables concatenation of extra info (email/phone/subsection) in the displayed label (optional)
 * @param {Function} dispatch - Redux dispatch function used to trigger async search actions (required)
 * @param {Function} setOpen - Controls the visibility of the options dropdown (required)
 * @param {Function|null} transformFn - Required function to transform API response into autocomplete options (optional)
 * @param {number|string} [maxLength=undefined] - Maximum number of items allowed.
 * @param {Boolean} disabled - If true, disables input and selection (optional)
 * @param {Function} onSearch - Custom search function for handling typed input (optional)
 * @param {Function} onKeyDown - Custom keyDown handler triggered on input key events (optional).
 * @param {number|string} [maxChar=undefined] - Maximum number of characters allowed.
 * @returns {JSX.Element}
 */
const CustomChipAutocomplete = ({
    options = [],
    setOptions,
    value = [],
    onChange,
    getOptionLabel,
    placeholder = '',
    userTypeIdsSearch = [],
    api,
    bodyApi = null,
    rowsPerPage = 20,
    model,
    keyId,
    showSubSection,
    dispatch,
    transformFn = null,
    maxLength,
    disabled,
    onSearch,
    onKeyDown,
    maxChar,
}) => {
    // State to track the current value typed in the input
    const [inputValue, setInputValue] = useState('');

    // State to manage whether the options dropdown is open
    const [open, setOpen] = useState(false);
    const isMaxReached = maxLength !== undefined && value.length >= parseInt(maxLength);
    const [inputWidth, setInputWidth] = useState(0);
    const [searchCompleted, setSearchCompleted] = useState(false);

    const { t } = useTranslation();

    //Handles the search logic whenever user types in the input
    const handleSearch = (search) => {
        setSearchCompleted(false);
        if (!search.trim()) {
            setOptions([]);
            setOpen(false);
            return;
        }

        setOptions([]);
        if (onSearch) {
            onSearch(search).then((result = []) => {
                const newOptions = transformFn ? transformFn(result, keyId, showSubSection) : result;

                setOptions(newOptions);
                setOpen(newOptions.length > 0);
                setSearchCompleted(true);
            });
            return;
        }

        handleApiSearch(search, userTypeIdsSearch);
    };

    // Calls API with search key and updates options
    const handleApiSearch = (search, userTypeIds) => {
        let body;
        // Build the request body: use default structure if bodyApi is null, otherwise merge search key with bodyApi
        if (bodyApi === null) {
            body = {
                key: search,
                pagination: { page: 0, size: rowsPerPage },
                userTypeIds,
                model,
            };
        } else {
            body = { ...bodyApi, key: search };
        }
        // Dispatch the search action depending on whether the API endpoint is public or not
        dispatch(api?.includes('public/') ? PublicCustomSearch({ body, api }) : CustomSearch({ body, api })).then(
            ({
                payload: {
                    data: { data },
                },
            }) => {
                // Transform the API response into autocomplete options if a transform function is provided
                const newOptions = transformFn
                    ? transformFn(data?.content || [], keyId, showSubSection)
                    : data?.content || [];

                setOptions(newOptions);
                setOpen(newOptions.length > 0);
            },
        );
    };

    // Debounce input changes to avoid excessive API calls
    useEffect(() => {
        const delay = setTimeout(() => handleSearch(inputValue), DEBOUNCE_DELAY);
        return () => clearTimeout(delay);
    }, [inputValue]);

    // Filter options locally based on input and remove already selected items
    const filteredOptions = options.filter((opt) => {
        const label = getOptionLabel(opt);
        if (!label) return false;

        return (
            label.toLowerCase().includes(inputValue.toLowerCase()) && !value.some((v) => getOptionLabel(v) === label)
        );
    });

    useEffect(() => {
        if (filteredOptions.length === 0) {
            setOpen(false);
        }
    }, [filteredOptions.length]);

    // Boolean flag to check if there are selected chips
    const hasChips = value.length > 0;
    //Handles selection of options, ensuring only unique items are selected
    const handleSelect = (event, newValue) => {
        const clean = newValue.filter((v) => typeof v !== 'string' || v.trim().length > 0);
        if (isMaxReached && clean.length > value.length) return;
        onChange(clean);
    };

    // Reference to the Autocomplete input element
    const autoRef = useRef(null);
    /**
     * Effect to blur the input when the maximum number of items is reached.
     * This prevents further typing or selection when maxLength is exceeded.
     */
    useEffect(() => {
        if (isMaxReached && autoRef.current) {
            autoRef.current.blur();
        }
    }, [isMaxReached]);
    useEffect(() => {
        if (autoRef.current) {
            setInputWidth(autoRef.current.offsetWidth);
        }
        const handleResize = () => {
            if (autoRef.current) {
                setInputWidth(autoRef.current.offsetWidth);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const CustomPopper = (props) => (
        <Popper {...props} anchorEl={autoRef.current} style={{ width: inputWidth }} placement='bottom-start' />
    );

    return (
        <>
            <Autocomplete
                multiple
                freeSolo
                PopperComponent={CustomPopper}
                disablePortal={false}
                options={filteredOptions}
                value={value}
                onChange={handleSelect}
                inputValue={inputValue}
                ref={autoRef}
                onInputChange={(e, val) => {
                    if (isMaxReached) return;
                    if (maxChar && val.length > maxChar) val = val.slice(0, maxChar);
                    setInputValue(val);
                }}
                getOptionLabel={getOptionLabel}
                filterSelectedOptions
                popupIcon={null}
                disabled={disabled}
                disableClearable
                clearIcon={null}
                open={isMaxReached ? false : open}
                slotProps={{
                    paper: { sx: styles.paper },
                    listbox: { sx: styles.listbox },
                }}
                renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                        <Box key={index} sx={styles.chipWrapper}>
                            <CustomChip
                                legend={getOptionLabel(option)}
                                background={colors.shades100}
                                fontColor={colors.general950}
                                borderColor={colors.shades300}
                                variant='large'
                                onDelete={() =>
                                    onChange(value.filter((v) => getOptionLabel(v) !== getOptionLabel(option)))
                                }
                                {...getTagProps({ index })}
                            />
                        </Box>
                    ))
                }
                renderInput={(params) => (
                    <Box sx={styles.inputWrapper(disabled, open, value)}>
                        <TextField
                            {...params}
                            variant='standard'
                            placeholder={value.length === 0 ? placeholder : ''}
                            InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                                inputProps: {
                                    ...params.inputProps,
                                    style: styles.inputDynamic(isMaxReached),
                                    onBlur: (e) => {
                                        if (value.length === 0) setOpen(false);
                                        params.inputProps?.onBlur?.(e);
                                    },
                                    onFocus: (e) => {
                                        if (!isMaxReached && filteredOptions.length > 0) setOpen(true);
                                        params.inputProps?.onFocus?.(e);
                                    },
                                },
                            }}
                            onKeyDown={(e) => onKeyDown?.(e, inputValue)}
                            sx={styles.input}
                        />

                        {inputValue.length === 0 ? (
                            <ArrowAccordionIcon className='arrow-toggle' sx={styles.arrowIcon(false, hasChips)} />
                        ) : (
                            <ArrowAccordionUpIcon className='arrow-toggle' sx={styles.arrowIcon(true)} />
                        )}
                    </Box>
                )}
                renderOption={(props, option) => (
                    <li {...props} key={getOptionLabel(option)} style={styles.optionItem}>
                        <CustomChip
                            legend={getOptionLabel(option)}
                            background={colors.shades100}
                            fontColor={colors.general950}
                            borderColor={colors.shades300}
                            variant='large'
                        />
                    </li>
                )}
            />
            {filteredOptions.length > 0 && value.length === 0 && (
                <FormHelperText sx={{...CustomStyleFormHelperText(false)}}>
                    {t('selectOptionText')}
                </FormHelperText>
            )}
            {(maxChar ||
                (searchCompleted &&
                    inputValue.trim().length > 0 &&
                    filteredOptions.length === 0 &&
                    value.length === 0)) && (
                <Box className='d-flex justify-content-between mt-1'>
                    {searchCompleted &&
                    inputValue.trim().length > 0 &&
                    filteredOptions.length === 0 &&
                    value.length === 0 ? (
                        <FormHelperText sx={{...CustomStyleFormHelperText(false)}}>
                            {t('please_enter_to_create')}
                        </FormHelperText>
                    ) : (
                        <div />
                    )}

                    {maxChar && (
                        <FormHelperText sx={{...CustomStyleFormHelperText(false)}}>
                            {inputValue.length}/{maxChar}
                        </FormHelperText>
                    )}
                </Box>
            )}
        </>
    );
};

export default CustomChipAutocomplete;
